import React, { useState, useRef, useEffect } from 'react';
import ProjectManager from '../master/project/ProjectMaster';
import TaskManagementApp from '../master/TaskManagement';

const SortAnalysisChart = () => {
  // State for resizable sections
  const [dimensions, setDimensions] = useState({
    projectsWidth: 50,
    tasksWidth: 50,
    subtasksWidth: 50,
    subsubtasksWidth: 50,
    topHeight: 50,
    bottomHeight: 50
  });

  // Refs for resizable sections
  const projectsRef = useRef(null);
  const tasksRef = useRef(null);
  const subtasksRef = useRef(null);
  const subsubtasksRef = useRef(null);
  const containerRef = useRef(null);

  // Resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [currentResizer, setCurrentResizer] = useState(null);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [startDimensions, setStartDimensions] = useState({});

  // Dummy data for subtasks and subsubtasks
  const dummySubtasks = [
    { id: 1, name: 'Design Review' },
    { id: 2, name: 'Code Implementation' },
    { id: 3, name: 'Testing Phase' }
  ];

  const dummySubsubtasks = [
    { id: 1, name: 'UI Components' },
    { id: 2, name: 'API Integration' },
    { id: 3, name: 'Unit Tests' }
  ];

  // Resizing functionality
  const startResize = (e, resizer) => {
    setIsResizing(true);
    setCurrentResizer(resizer);
    setStartPosition({ x: e.clientX, y: e.clientY });
    setStartDimensions(dimensions);
  };

  const handleResize = (e) => {
    if (!isResizing) return;

    const container = containerRef.current;
    const deltaX = e.clientX - startPosition.x;
    const deltaY = e.clientY - startPosition.y;
    
    const newDimensions = { ...startDimensions };
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    switch(currentResizer) {
      case 'vertical-projects':
        newDimensions.projectsWidth = Math.max(20, Math.min(80, 
          startDimensions.projectsWidth + (deltaX / containerWidth) * 100
        ));
        newDimensions.tasksWidth = 100 - newDimensions.projectsWidth;
        break;
        
      case 'vertical-subtasks':
        newDimensions.subtasksWidth = Math.max(20, Math.min(80, 
          startDimensions.subtasksWidth + (deltaX / containerWidth) * 100
        ));
        newDimensions.subsubtasksWidth = 100 - newDimensions.subtasksWidth;
        break;
        
      case 'horizontal':
        newDimensions.topHeight = Math.max(20, Math.min(80, 
          startDimensions.topHeight + (deltaY / containerHeight) * 100
        ));
        newDimensions.bottomHeight = 100 - newDimensions.topHeight;
        break;
    }

    setDimensions(newDimensions);
  };

  const stopResize = () => {
    setIsResizing(false);
    setCurrentResizer(null);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', stopResize);
      return () => {
        document.removeEventListener('mousemove', handleResize);
        document.removeEventListener('mouseup', stopResize);
      };
    }
  }, [isResizing]);

  return (
    <div ref={containerRef} className="flex flex-col h-screen max-h-screen">
      {/* Top Row */}
      <div className="flex border-b relative" style={{ height: `${dimensions.topHeight}%` }}>
        {/* Projects Section */}
        <div 
          ref={projectsRef}
          className="overflow-auto relative"
          style={{ width: `${dimensions.projectsWidth}%` }}
        >
          <ProjectManager />
          <div
            className="absolute right-0 top-0 bottom-0 w-2 bg-gray-300 hover:bg-blue-400 cursor-col-resize"
            onMouseDown={(e) => startResize(e, 'vertical-projects')}
          />
        </div>

        {/* Tasks Section */}
        <div 
          ref={tasksRef}
          className="overflow-auto relative"
          style={{ width: `${dimensions.tasksWidth}%` }}
        >
          <TaskManagementApp />
        </div>
      </div>

      {/* Horizontal Resizer */}
      <div
        className="w-full h-2 bg-gray-300 hover:bg-blue-400 cursor-row-resize"
        onMouseDown={(e) => startResize(e, 'horizontal')}
      />

      {/* Bottom Row */}
      <div className="flex relative" style={{ height: `${dimensions.bottomHeight}%` }}>
        {/* Subtasks Section */}
        <div 
          ref={subtasksRef}
          className="overflow-auto relative"
          style={{ width: `${dimensions.subtasksWidth}%` }}
        >
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-4">Subtasks</h2>
            <div className="space-y-2">
              {dummySubtasks.map(subtask => (
                <div key={subtask.id} className="border rounded p-3 bg-gray-50">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{subtask.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            className="absolute right-0 top-0 bottom-0 w-2 bg-gray-300 hover:bg-blue-400 cursor-col-resize"
            onMouseDown={(e) => startResize(e, 'vertical-subtasks')}
          />
        </div>

        {/* Subsubtasks Section */}
        <div 
          ref={subsubtasksRef}
          className="overflow-auto"
          style={{ width: `${dimensions.subsubtasksWidth}%` }}
        >
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-4">Subsubtasks</h2>
            <div className="space-y-2">
              {dummySubsubtasks.map(subsubtask => (
                <div key={subsubtask.id} className="border rounded p-3 bg-gray-50">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{subsubtask.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SortAnalysisChart;